import { BrowserAuthError, InteractionRequiredAuthError } from '@azure/msal-browser'
import { adminMsalInstance, adminLoginRequest } from './adminMsal'

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '/api/v1'

let initialized: Promise<void> | null = null

// adminMsalInstance is only initialized lazily, on first use from within
// the admin route -- unlike the tenant-facing msalInstance (initialized
// unconditionally in main.tsx for every visitor), there is no reason to
// pay MSAL's initialization cost for a route almost nobody visits.
async function ensureInitialized(): Promise<void> {
  if (!initialized) initialized = adminMsalInstance.initialize()
  return initialized
}

export async function adminLogin(): Promise<void> {
  await ensureInitialized()
  // Popup, not redirect: unlike the tenant-facing app's redirectToLogin
  // (lib/api.ts), this console is a single, deliberately-visited page, not
  // something users navigate away from and back to repeatedly -- a popup
  // keeps the admin's place on the page across sign-in instead of a full
  // navigation round-trip.
  await adminMsalInstance.loginPopup(adminLoginRequest)
}

export function adminLogout(): void {
  const account = adminMsalInstance.getActiveAccount() ?? adminMsalInstance.getAllAccounts()[0] ?? null
  void adminMsalInstance.logoutPopup({ account })
}

export function getAdminAccount() {
  return adminMsalInstance.getActiveAccount() ?? adminMsalInstance.getAllAccounts()[0] ?? null
}

async function getAdminAccessToken(): Promise<string> {
  await ensureInitialized()
  const account = getAdminAccount()
  if (!account) throw new Error('Not signed in to the admin console')

  try {
    const result = await adminMsalInstance.acquireTokenSilent({ ...adminLoginRequest, account })
    return result.accessToken
  } catch (err) {
    // acquireTokenSilent falls back to a hidden iframe on the redirect URI,
    // which the SWA's X-Frame-Options: DENY blocks -- MSAL then times out
    // with a BrowserAuthError rather than InteractionRequiredAuthError.
    if (err instanceof InteractionRequiredAuthError || err instanceof BrowserAuthError) {
      const result = await adminMsalInstance.acquireTokenPopup(adminLoginRequest)
      return result.accessToken
    }
    throw err
  }
}

export class AdminApiError extends Error {
  constructor(message: string, public readonly status: number, public readonly code?: string) {
    super(message)
    this.name = 'AdminApiError'
  }
}

async function adminRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = await getAdminAccessToken()

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  })

  if (!response.ok) {
    // Mirrors withAdminAuth.ts's own deny-by-default contract: every auth
    // failure on this API surface is an identical 403 with no distinguishing
    // detail (see that file's header comment for why) -- this client has
    // nothing more specific to show for those cases than "Forbidden"
    // either, and must not try to invent detail the server deliberately
    // didn't send.
    const body = await response.json().catch(() => ({})) as { error?: { message?: string; code?: string } }
    throw new AdminApiError(body.error?.message ?? response.statusText, response.status, body.error?.code)
  }

  if (response.status === 204) return undefined as T
  return response.json() as Promise<T>
}

export const adminApi = {
  get: <T>(path: string) => adminRequest<T>(path, { method: 'GET' }),
  post: <T>(path: string, body?: unknown) => adminRequest<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  put: <T>(path: string, body?: unknown) => adminRequest<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
}
